import React, { createContext, useContext, useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { login as apiLogin, register as apiRegister, getMe, logout as apiLogout } from '../api';
import { track, setPersonProps, trackAdsConversion, trackOpenAIConversion, Events } from '../analytics';
import { getAttribution } from '../attribution';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // { id, username, email, profile: { tier } }
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      getMe(token)
        .then((res) => {
          setUser(res.data);
          posthog.identify(String(res.data.id), {
            username: res.data.username,
            email: res.data.email,
          });
        })
        .catch(() => {
          localStorage.removeItem('token');
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  // When any request 401s, api.js clears the stale token and fires this event;
  // reset auth state so the UI reflects logged-out immediately (no reload needed).
  useEffect(() => {
    const handleUnauthorized = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const login = async (email, password) => {
    const res = await apiLogin({ email, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    posthog.identify(String(res.data.user.id), {
      username: res.data.user.username,
      email: res.data.user.email,
    });
    track(Events.LOGIN);
    return res.data;
  };

  const register = async (email, password, confirmPassword) => {
    // Ad-click attribution rides along with signup so the backend can report a
    // purchase conversion later, whenever the user actually subscribes.
    const attribution = getAttribution();
    const res = await apiRegister({
      email,
      password,
      confirm_password: confirmPassword,
      ...(attribution ? { attribution } : {}),
    });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    posthog.identify(String(res.data.user.id), {
      username: res.data.user.username,
      email: res.data.user.email,
    });
    // signup_date as a durable person property so retention cohorts can be built by signup day.
    setPersonProps({ signup_date: new Date().toISOString() });
    track(Events.SIGNUP);
    trackAdsConversion('SIGNUP');
    trackOpenAIConversion('registration_completed');
    return res.data;
  };

  const logout = async () => {
    try { await apiLogout(token); } catch {}
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    posthog.reset();
  };

  const refreshUser = async () => {
    if (token) {
      const res = await getMe(token);
      setUser(res.data);
    }
  };

  const tier = user?.profile?.tier || null; // 'free' | 'basic' | 'pro' | null (guest)
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, token, tier, isAuthenticated, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
